/*
 * NAO-97 T7. <naomi-length-guide> plus the chain rows of snippets/pdp-length.liquid.
 * Port of naomi-ops mocks/pdp/length-guide (geometry.js, then length-guide.js), signed off
 * by the owner after three rounds: keep the drape calibration, the centred 1.6x view,
 * the label sizes, the collarbones tied into the shoulder line and the 1px strokes.
 * Styles for the element live in assets/pdp-length.css (the mock injected them).
 *
 *   <naomi-length-guide selected="40-42cm (15.7-16.5&quot;)">
 *     <script type="application/json">[{"value":"40-42cm (15.7-16.5\")","lengths":[40,42]}, ...]</script>
 *   </naomi-length-guide>
 *
 * value = the variant option value, lengths = every length it can be worn at in cm (full length = the max).
 * A `cm-only` attribute (KR store) drops the inch line under each label.
 * Set .selected (or the attribute) to sync from the page. Display only (owner 2026-09-27): nothing in it
 * is tappable, the buy box chooses the chain. No `selected` (a size guide page) draws every length in ink.
 * Drawn in pixel units on every resize, so strokes stay exactly 1px and labels 12px at any width.
 */

// ---- geometry.js: the figure and the drape model, in centimetres.
// Front view, y down, origin at the suprasternal notch (the hollow between the collarbones).
window.NLG = window.NLG || (() => {
  // ---- body, right half (the left half is its mirror). A slim adult neck, about 10cm across,
  // shoulders about 39cm (biacromial ~35cm plus the deltoid), acromion level with the notch.
  const OUTLINE = [[4.72, -9.5],
    [4.76, -7.6, 4.92, -6.0, 5.32, -4.98],     // neck, widening gently to its base
    [5.8, -3.95, 7.0, -3.5, 9.0, -2.98],       // a long soft turn into the trapezius
    [11.8, -2.3, 14.6, -1.6, 16.3, -0.8],      // trapezius slope, falling to the shoulder
    [17.6, -0.15, 18.2, 1.0, 18.3, 2.4],       // over the shoulder
    [18.4, 3.4, 18.45, 4.4, 18.5, 5.6]];       // the arm begins, the line lifts off
  // collarbones: shallow S-curves, heads nearly meeting either side of the notch, running out under the
  // skin to meet the shoulder contour at the acromion (the outline point 16.3, -0.8), never a free arc
  const CLAVICLE = [[0.85, 0.12], [2.3, 0.95, 4.4, 1.1, 6.9, 0.6], [10, 0.05, 13.6, -0.45, 16.3, -0.8]];

  // ---- drape model. The loop is split at the two points where the chain crests the neck/trapezius
  // corner (the anchors, on the outline). Behind them the chain lies around the nape (BACK cm).
  // In front, each half runs over the body from the anchor to the centre front: a quarter ellipse in 3D
  // whose horizontal axis is the anchor's half width and whose front axis is how far the body sits
  // forward of the anchors (Z_NECK at the notch, growing Z_SLOPE per cm lower as the chest comes forward).
  // Seen from the front that is exactly half an ellipse, anchor to anchor, so it draws as one SVG arc.
  // ponytail: calibration knob. Tuned so 40cm sits at the notch, 42 under the collarbone heads,
  // 45 below the collarbones, 50 about 6.5cm down (site copy + US charts); valid 38 to 60cm.
  const BACK = 18.4, Z_NECK = 6, Z_SLOPE = 0.3;
  const bez = (p0, c, t) => { const u = 1 - t; return [0, 1].map(i => u * u * u * p0[i] + 3 * u * u * t * c[i] + 3 * u * t * t * c[i + 2] + t * t * t * c[i + 4]); };
  const [AX, AY] = bez(OUTLINE[1].slice(4), OUTLINE[2], 0.3);
  const quarter = b => {
    const f = Math.hypot(b, Z_NECK + Z_SLOPE * Math.max(0, AY + b)); let s = 0; const n = 64;
    for (let i = 0; i < n; i++) { const t = (i + 0.5) / n * Math.PI / 2; s += Math.hypot(AX * Math.sin(t), f * Math.cos(t)); }
    return s * Math.PI / 2 / n;
  };
  // vertical semi-axis of the front arc for a chain of L cm; its lowest point is at AY + b
  const drop = L => {
    const half = (L - BACK) / 2; let lo = 0, hi = 60;
    for (let i = 0; i < 40; i++) { const m = (lo + hi) / 2; quarter(m) < half ? lo = m : hi = m; }
    return Math.max(lo, 1);   // under ~38cm the chain rides up the neck; kept as a shallow curve
  };

  // Front arc as two cubics, anchor to centre front. A half ellipse would be widest at the anchors;
  // a real chain spreads a little over the trapezius first, so the first control pushes out (BULGE per cm of drop).
  const K = 0.5523, BULGE = 0.08;
  const half = L => { const b = drop(L); return [[AX, AY], [AX + Math.min(1.2, BULGE * b), AY + K * b, K * AX, AY + b, 0, AY + b]]; };

  // ---- labels
  const inch = L => `${+(L / 2.54).toFixed(1)} in`;

  // ---- variants: [{ value, lengths: [40, 42] }]. Positions are the union of every length sold.
  const positions = vs => [...new Set(vs.flatMap(v => v.lengths))].sort((a, b) => a - b);

  // ---- drawing helpers. A view is { x0, x1, y0, y1 } in cm; fit() maps it to a pixel width.
  const fit = (view, w) => {
    const s = w / (view.x1 - view.x0), h = Math.round((view.y1 - view.y0) * s);
    const X = x => (x - view.x0) * s, Y = y => (y - view.y0) * s;
    const seg = (pts, sx = 1) => { const [p, ...cs] = pts; return `M${X(sx * p[0]).toFixed(2)} ${Y(p[1]).toFixed(2)}` +
      cs.map(c => `C${[0, 2, 4].map(i => `${X(sx * c[i]).toFixed(2)} ${Y(c[i + 1]).toFixed(2)}`).join(' ')}`).join(''); };
    const both = pts => seg(pts, 1) + seg(pts, -1);
    const arc = L => {   // anchor to anchor
      const [p, c] = half(L);
      return seg(half(L), -1) + seg([[0, c[5]], [c[2], c[3], c[0], c[1], p[0], p[1]]]).replace(/^M[^C]+/, '');
    };
    return { s, h, X, Y, both, arc };
  };

  return { OUTLINE, CLAVICLE, bottom: L => AY + drop(L), inch, positions, fit };
})();

// ---- length-guide.js: the element.
(() => {
  if (customElements.get('naomi-length-guide')) return;
  const N = window.NLG, INK = 'var(--n-ink, #111110)', TAUPE = 'var(--n-taupe-deep, #6B625B)';
  const esc = v => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const px = v => v.toFixed(1), crisp = v => Math.round(v) + 0.5;
  const LINE = `fill="none" stroke="${INK}" stroke-width="1"`;

  class LengthGuide extends HTMLElement {
    static n = 0;
    static observedAttributes = ['selected'];
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      const j = this.querySelector('script[type="application/json"]');
      if (j) this.variants = JSON.parse(j.textContent);
      // the label boxes are measured, so measure again once the webfont is in
      document.fonts?.ready.then(() => this.draw());
      new ResizeObserver(() => { if (this.clientWidth !== this.w) this.draw(); }).observe(this);
    }
    attributeChangedCallback() { this.draw(); }
    get selected() { return this.getAttribute('selected'); }
    set selected(v) { this.setAttribute('selected', v); }
    draw() {
      const w = Math.round(this.clientWidth); if (!w || !this.variants) return; this.w = w;
      const vs = this.variants, pos = N.positions(vs), deep = Math.max(...pos);
      const chosen = vs.find(v => v.value === this.selected), sel = chosen;
      const on = L => !sel || sel.lengths.includes(L), full = sel ? Math.max(...sel.lengths) : null;
      // The whole figure, centred: jaw-line crop at the top, both shoulders inside the frame.
      const id = this.uid || (this.uid = 'lg' + (++LengthGuide.n)), top = -9;
      const f = N.fit({ x0: -11.8, x1: 11.8, y0: top, y1: Math.max(N.bottom(deep) + 4, 8) }, w);
      const cx = crisp(f.X(0));
      // one line, lifting off at the shoulders: stroke opacity fades outward, never over 1px
      const fade = (k, a, b) => `<linearGradient id="${id}${k}" gradientUnits="userSpaceOnUse" x1="${px(f.X(-a))}" x2="${px(f.X(a))}" y1="0" y2="0">` +
        `<stop offset="0" stop-color="#111110" stop-opacity="${b}"/><stop offset=".4" stop-color="#111110" stop-opacity="${a > 17 ? .7 : .34}"/>` +
        `<stop offset=".6" stop-color="#111110" stop-opacity="${a > 17 ? .7 : .34}"/><stop offset="1" stop-color="#111110" stop-opacity="${b}"/></linearGradient>`;
      let s = `<defs>${fade('o', 18.5, .12)}${fade('c', 16.3, .3)}</defs>` +
        `<path d="${f.both(N.OUTLINE)}" fill="none" stroke="url(#${id}o)" stroke-width="1"/>` +
        `<path d="${f.both(N.CLAVICLE)}" fill="none" stroke="url(#${id}c)" stroke-width="1"/>`;

      // chains: every length sold is a quiet line; the selected chain is ink at its full length
      s += pos.map(L => { const ink = !sel || L === full;
        return `<path d="${f.arc(L)}" ${LINE.replace(INK, ink ? INK : TAUPE)} opacity="${ink ? (sel ? 1 : .42) : .3}"/>`; }).join('');
      const loops = sel ? sel.lengths.filter(L => L !== full) : [];
      // where the chain fastens shorter: a small solid dot at that length's lowest point
      s += loops.map(L => `<circle cx="${cx}" cy="${px(f.Y(N.bottom(L)))}" r="2" fill="${INK}"/>`).join('');
      // a small generic pendant ring, hung from the selected chain's lowest point
      if (sel) s += `<circle cx="${cx}" cy="${px(f.Y(N.bottom(full)) + 2.7)}" r="2.2" fill="#FFF" stroke="${INK}" stroke-width="1"/>`;

      // labels alternate sides, each at its chain's true depth, so 1.3cm steps stay apart even at phone scale.
      // A very quiet dotted rule runs from the label to the lowest point of its chain.
      const xl = cx - f.s * 6.7, xr = cx + f.s * 6.7;
      const cmOnly = this.hasAttribute('cm-only');   // KR store: cm only, no inch line
      s += pos.map((L, i) => {
        const left = i % 2 === 0, y = f.Y(N.bottom(L)), c = on(L) ? INK : TAUPE, hit = loops.includes(L) ? 4 : 3;
        const x = left ? xl : xr, a = left ? 'end' : 'start', sign = left ? -1 : 1;
        return `<g>` +
          `<path d="M${px(x - sign * 6)} ${crisp(y)}H${px(cx + sign * hit)}" stroke="${c}" stroke-width="1" stroke-dasharray="1 3" opacity="${on(L) ? .4 : .22}"/>` +
          `<text class="t0" x="${px(x)}" y="${px(y + 4.5)}" text-anchor="${a}"><tspan fill="${c}">${L}cm</tspan>` +
          (cmOnly ? '' : `<tspan x="${px(x)}" dy="15" font-size="12" fill="${TAUPE}" opacity="${on(L) ? 1 : .8}">${N.inch(L)}</tspan>`) +
          `</text></g>`; }).join('');
      // aria text from locale keys (pdp-length.liquid data-sr-*), placeholders swapped here
      const d = this.dataset, fill = (t, k, v) => (t || '').replace(`{${k}}`, v);
      const label = esc(fill(d.srFigure, 'lengths', pos.map(L => L + 'cm').join(', ')) +
        (chosen ? ' ' + fill(d.srSelected, 'value', chosen.value) : ''));
      this.querySelectorAll('svg').forEach(e => e.remove());
      this.insertAdjacentHTML('afterbegin', `<svg viewBox="0 0 ${w} ${f.h}" width="${w}" height="${f.h}" role="img" aria-label="${label}">${s}</svg>`);
      // each label sits on a plain white box, measured once drawn, so the lines under it
      // stop at its edge (a stroke halo with paint-order cut through the glyphs on iOS Safari)
      this.querySelectorAll('text.t0').forEach(t => {
        const b = t.getBBox(); if (!b.width) return;
        const r = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        [['x', b.x - 3], ['y', b.y - 1], ['width', b.width + 6], ['height', b.height + 2]].forEach(([k, v]) => r.setAttribute(k, px(v)));
        r.setAttribute('fill', '#FFF');
        t.before(r);
      });
    }
  }
  customElements.define('naomi-length-guide', LengthGuide);
})();

// ---- page wiring: the rows and the drawing follow the variant the buy box holds.
(() => {
  const PDP = window.NaomiPDP;
  if (!PDP) return;
  document.querySelectorAll('[data-pdp-length]').forEach((block) => {
    if (block.dataset.wired) return;
    block.dataset.wired = '1';
    const guide = block.querySelector('naomi-length-guide');
    const rows = [...block.querySelectorAll('[data-value]')];
    // compound options ("Chain Color & Length") have no length role: their rows carry variant ids instead
    const byVariant = rows.some((r) => r.dataset.variants != null);
    PDP.on((state) => {
      const value = byVariant
        ? rows.find((r) => r.dataset.variants.split(' ').includes(String(state.variantId)))?.dataset.value
        : state.options.length;
      if (value == null) return;
      rows.forEach((r) => (r.dataset.value === value ? r.setAttribute('aria-current', 'true') : r.removeAttribute('aria-current')));
      if (guide && guide.selected !== value) guide.selected = value;
    });
  });
})();
